/**
 * @author MrZenW
 * @email MrZenW@Gmail.com, https://MrZenW.com
 * @create date 2021-07-23 16:18:42
 * @modify date 2021-07-23 16:18:42
 * @desc [description]
 */

import { ApiService, HttpResponse, HttpResponseData } from '../api_service';

export interface UserDoc {
  _id: string,
  email: string,
  subscribedAt?: string | null,
  subscriptionLifetime?: number,
  [propName: string]: any,
}

export interface UserSecurityDoc {
  [propName: string]: any,
}

export interface AdminDoc {
  role: string,
  [propName: string]: any,
}

export interface TeacherReview {
  _id: string,
  kind: 'signup' | 'existing',
  email: string,
  name?: string,
  reason: string,
  requestedAt: string,
  privacyPolicyAcknowledgedAt?: string,
  rejectedAt?: string,
  rejectedBy?: string,
  approvedAt?: string,
  approvedBy?: string,
  status?: 'pending' | 'approved' | 'rejected',
  adminReviewActivity?: any,
  history?: any[],
  setupStatus?: 'pending' | 'processing' | 'complete',
  setupRetryAvailable?: boolean,
}

export interface AccountReviewActivity {
  _id: string,
  type: 'account.approved' | 'account.rejected' | 'account.reverted',
  at: string,
  actor: { userId: string, email: string },
  subject: { kind: 'signup' | 'existing', id: string, email: string },
}

export interface TeacherReviewsResponse extends HttpResponse {
  data: HttpResponseData & {
    reviews?: TeacherReview[], activities?: AccountReviewActivity[], limitPerType?: number,
    totalCount?: number, totalPages?: number, pageSize: number, nextCursor: string | null,
    counts: { pending: number, approved: number, rejected: number },
  },
}

export interface UserInfoResponseData extends HttpResponseData {
  userDoc: UserDoc,
  userSecurityDoc?: UserSecurityDoc,
  adminDoc?: AdminDoc,
  adminRole?: string,
}

export interface UserInfoResponse extends HttpResponse {
  data: UserInfoResponseData,
}

export interface UserRegistrationData {
  email: string,
  password: string,
  googleReCaptchaToken?: string,
  privacyPolicyAccepted: boolean,
}

export interface UserLoginData {
  email: string,
  password: string,
  googleReCaptchaToken?: string,
  privacyPolicyAccepted: boolean,
}

// Some API versions return the account restriction in _err_ instead of _code_.
export function getTeacherSignupReviewCode(requestError: { data?: HttpResponseData }): string | undefined {
  const reviewCodes = ['REVIEW_REQUIRED', 'GOOGLE_SSO_REQUIRED', 'TEACHER_ACCESS_DENIED', 'CLASSROOM_PERMISSION_REQUIRED'];
  return [requestError?.data?._code_, requestError?.data?._err_]
    .find((code) => typeof code === 'string' && reviewCodes.includes(code)) as string | undefined;
}

// google oauth
export interface GoogleOAuthDoc {
  sub: string,
  accessToken: string,
  email: string,
  emailVerified: boolean,
  familyName: string,
  givenName: string,
  googleId: string,
  picture: string,
  locale: string,
  name: string,
  hd: string,
  tokenId: string,
  tokenObj: any,
  classroomOAuthCode: string,
  classroomOAuthToken: string,
}

export interface GoogleClassroomAuthorizeResponseData extends HttpResponseData {
  googleOAuthDoc: GoogleOAuthDoc,
}

export interface GoogleClassroomAuthorizeResponse extends HttpResponse {
  data: GoogleClassroomAuthorizeResponseData,
}

export interface RecoverEmailDoc {
  userId: string,
  recoverCode?: string,
}

export interface SendRecoverEmailResponseData extends HttpResponseData {
  recoverEmailDoc: RecoverEmailDoc,
}
export interface SendRecoverEmailResponse extends HttpResponse {
  data: SendRecoverEmailResponseData,
}

export interface ResetPasswordResponseData extends HttpResponseData {
  ok: boolean,
}
export interface ResetPasswordResponse extends HttpResponse {
  data: ResetPasswordResponseData,
}

export interface GoogleClassroomCourseItem {
  alternateLink: string // "https://classroom.google.com/c/MTQ1ODUyMDY1ODUx"
  calendarId: string // "c_classroom6139b004@group.calendar.google.com"
  courseGroupEmail: string // "test_eae1b351@3dprintingsystems.com"
  courseState: string // "ACTIVE"
  creationTime: string // "2020-09-22T04:43:08.536Z"
  descriptionHeading: string // "test"
  enrollmentCode: string // "cml7nsc"
  guardiansEnabled: boolean // false
  id: string // "145852065851"
  name: string // "test"
  ownerId: string // "103822316527998172136"
  teacherFolder: {
    alternateLink: string // "https://drive.google.com/drive/folders/0B7ucDpnWNIcUfjFDYWRUanRJa29IcTFFMk0tM2VwQm9Oam9UV3BBWXhUMXhLZ3R2UnJ0M2s?resourcekey=0-EaT3hT97Xa_mazYtlVmxqQ"
    id: string // "0B7ucDpnWNIcUfjFDYWRUanRJa29IcTFFMk0tM2VwQm9Oam9UV3BBWXhUMXhLZ3R2UnJ0M2s"
    title: string // "test"
    [propName: string]: any
  }
  teacherGroupEmail: string // "test_teachers_8b968f74@3dprintingsystems.com"
  updateTime: string // "2021-12-09T21:36:09.928Z"
}
export interface GoogleClassroomCourseListResponseData extends HttpResponseData {
  courseList: GoogleClassroomCourseItem[],
}

export interface GoogleClassroomCourseListResponse extends HttpResponse {
  data: GoogleClassroomCourseListResponseData,
}

// student
export interface GoogleClassroomStudentItem {
  courseId: string // "145852065851"
  profile: {
    emailAddress: string // "bruce@3dprintingsystems.com"
    id: string // "116563919706397479378"
    name: {
      familyName: string // "Jackson"
      fullName: string // "Bruce Jackson"
      givenName: string // "Bruce"
      [propName: string]: any,
    }
    permissions: {
      permission: string // "CREATE_COURSE"
      [propName: string]: any,
    }[]
    photoUrl: string // "//lh3.googleusercontent.com/a-/AOh14GhHjXbw-I5COAG2Y7ApsKAydFU70W5LuOTyRKKkjo4"
    [propName: string]: any,
  }
  userId: string // "116563919706397479378"
  [propName: string]: any,
}
export interface GoogleClassroomStudentListResponseData extends HttpResponseData {
  studentList: GoogleClassroomStudentItem[]
}

export interface GoogleClassroomStudentListResponse extends HttpResponse {
  data: GoogleClassroomStudentListResponseData,
}

export class UserApiClass {
  apiService: ApiService = null;
  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }
  private requirePrivacyPolicy(accepted: boolean) {
    if (accepted !== true) {
      throw Object.assign(new Error('Please accept the privacy policy before signing in or signing up.'), {
        status: 400,
        data: { _code_: 'PRIVACY_POLICY_ACKNOWLEDGEMENT_REQUIRED', _err_: 'Please accept the privacy policy before signing in or signing up.' },
      });
    }
  }
  getMyUserInfo(): Promise<UserInfoResponse> {
    return this.apiService.clientGet('/user/myDoc') as Promise<UserInfoResponse>;
  }
  getTeacherReviews(status: 'pending' | 'approved' | 'rejected' = 'pending', search = '', cursor?: string): Promise<TeacherReviewsResponse> {
    return this.apiService.clientGet('/user/teacher-reviews', { status, q: search, ...(cursor ? { cursor } : {}) }, null, { responseErrorUse: false }) as Promise<TeacherReviewsResponse>;
  }
  getTeacherReviewActivity(cursor?: string): Promise<TeacherReviewsResponse> {
    return this.apiService.clientGet('/user/teacher-reviews/activity', cursor ? { cursor } : null, null, { responseErrorUse: false }) as Promise<TeacherReviewsResponse>;
  }
  rejectTeacherReview(review: TeacherReview, options: any = {}): Promise<HttpResponse> {
    return this.apiService.clientPost(`/user/teacher-reviews/${review.kind}/${encodeURIComponent(review._id)}/reject`, null, options, { responseErrorUse: false }) as Promise<HttpResponse>;
  }
  getTeacherReview(review: Pick<TeacherReview, 'kind' | '_id'>): Promise<HttpResponse> {
    return this.apiService.clientGet('/user/teacher-reviews/' + review.kind + '/' + encodeURIComponent(review._id), null, null, { responseErrorUse: false });
  }
  revertTeacherReview(review: TeacherReview, options: any = {}): Promise<HttpResponse> {
    return this.apiService.clientPost('/user/teacher-reviews/' + review.kind + '/' + encodeURIComponent(review._id) + '/revert', null, options, { responseErrorUse: false });
  }
  getAdminRoles(): Promise<HttpResponse> {
    return this.apiService.clientGet('/user/admin-roles', null, null, { responseErrorUse: false }) as Promise<HttpResponse>;
  }
  assignAdminRole(email: string, role: 'admin' | 'super_admin'): Promise<HttpResponse> {
    return this.apiService.clientPost('/user/admin-roles', null, { email, role }, { responseErrorUse: false }) as Promise<HttpResponse>;
  }
  approveTeacherReview(review: TeacherReview, options: any = {}): Promise<HttpResponse> {
    return this.apiService.clientPost(`/user/teacher-reviews/${review.kind}/${encodeURIComponent(review._id)}/approve`, null, options, {
      responseErrorUse: false,
    }) as Promise<HttpResponse>;
  }
  private async withAccountLoading<T>(title: string, request: () => Promise<T>): Promise<T> {
    alertLib.fire({
      title,
      text: 'Please wait while we check your account.',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => alertLib.showLoading(),
    });
    try {
      return await request();
    } finally {
      alertLib.close();
    }
  }
  registerUser(userRegistrationData: UserRegistrationData): Promise<UserInfoResponse> {
    this.requirePrivacyPolicy(userRegistrationData.privacyPolicyAccepted);
    return this.withAccountLoading('Creating your account', () => this.apiService.clientPost('/user/sign-up', null, userRegistrationData, {
      responseErrorUse: false,
    }) as Promise<UserInfoResponse>);
  }
  login(userLoginData: UserLoginData): Promise<UserInfoResponse> {
    this.requirePrivacyPolicy(userLoginData.privacyPolicyAccepted);
    return this.withAccountLoading('Signing you in', () => this.apiService.clientPost('/user/login', null, userLoginData, {
      responseErrorUse: false,
    }) as Promise<UserInfoResponse>);
  }
  logout(): Promise<HttpResponse> {
    return this.apiService.clientGet('/user/logout') as Promise<HttpResponse>;
  }
  async loginWithGoogle(googleLoginInfo: any, privacyPolicyAccepted: boolean): Promise<UserInfoResponse> {
    this.requirePrivacyPolicy(privacyPolicyAccepted);
    const attempt = (accessToken: string): Promise<UserInfoResponse> => this.withAccountLoading('Checking your Google account', () => this.apiService.clientPost(
      '/user/login-with-google', null, { accessToken, privacyPolicyAccepted }, { responseErrorUse: false },
    ) as Promise<UserInfoResponse>);
    let blockedError: any;
    try {
      return await attempt(googleLoginInfo.accessToken);
    } catch (requestError) {
      blockedError = requestError;
    }
    if (getTeacherSignupReviewCode(blockedError) === 'CLASSROOM_PERMISSION_REQUIRED' && typeof googleLoginInfo.grant === 'function') {
      const consent = await alertLib.alertInfo({
        title: 'Verify teacher access',
        text: 'Allow Google Classroom profile access so we can verify your new teacher account.',
        showCancelButton: true,
        confirmButtonText: 'Continue with Google',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            return await googleLoginInfo.grant({ scope: 'https://www.googleapis.com/auth/classroom.profile.emails' });
          } catch (error) {
            alertLib.showValidationMessage('Google Classroom permission was not granted. Please try again or cancel.');
            return false;
          }
        },
      });
      if (consent.isConfirmed) {
        const googleUser = consent.value || googleLoginInfo;
        try {
          return await attempt(googleUser.getAuthResponse(true).access_token);
        } catch (requestError) {
          blockedError = requestError;
        }
      }
    }
    if (getTeacherSignupReviewCode(blockedError)) {
      await this.showTeacherSignupReviewPrompt(blockedError, privacyPolicyAccepted);
    }
    throw blockedError;
  }
  async showTeacherSignupReviewPrompt(requestError: any, privacyPolicyAccepted: boolean): Promise<void> {
    this.requirePrivacyPolicy(privacyPolicyAccepted);
    requestError.reviewPromptHandled = true;
    const emailRequest = ['REVIEW_REQUIRED', 'GOOGLE_SSO_REQUIRED'].includes(getTeacherSignupReviewCode(requestError));
    const choice = await alertLib.alertInfo({
      title: 'Account review required',
      text: emailRequest
        ? `We couldn’t spot a Google Classroom on this account. Submit your email below for review, and we’ll have you ready to go shortly!`
        : 'We couldn’t automatically confirm your teacher status with Google, but we’re on it! Submit your details for a quick review, and our team will get your account approved and ready to go.',
      showCancelButton: true,
      confirmButtonText: 'Submit for review',
      cancelButtonText: 'Cancel',
      focusCancel: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !alertLib.isLoading(),
      allowEscapeKey: () => !alertLib.isLoading(),
      preConfirm: async () => {
        try {
          await this.apiService.clientPost('/user/request-teacher-review', null, {}, { responseErrorUse: false });
          return true;
        } catch (error) {
          const response = error as HttpResponse;
          alertLib.showValidationMessage(typeof response?.data?._err_ === 'string'
            ? response.data._err_ : 'Unable to send your request. Please try again.');
          return false;
        }
      },
    });
    if (choice.isConfirmed) {
      await alertLib.alertSuccess({ title: 'Review request submitted', text: 'Your account creation request has been sent to our support team. They will contact you at the submitted email address after reviewing your eligibility. This request does not create an account or grant access.' });
    }
  }
  loginStudentWithGoogle(googleLoginInfo: any, roomCode: string, privacyPolicyAccepted: boolean): Promise<HttpResponse> {
    this.requirePrivacyPolicy(privacyPolicyAccepted);
    return this.apiService.clientPost('/user/login-student-with-google', null, {
      accessToken: googleLoginInfo.accessToken,
      roomCode,
      privacyPolicyAccepted,
    }, { responseErrorUse: false }) as Promise<HttpResponse>;
  }
  googleClassroomAuthorize(oAuthCode: string): Promise<GoogleClassroomAuthorizeResponse> {
    return this.apiService.clientGet(`/user/google-classroom/authorize`, { oAuthCode }) as Promise<GoogleClassroomAuthorizeResponse>;
  }
  googleClassroomCourseList(): Promise<GoogleClassroomCourseListResponse> {
    return this.apiService.clientGet('/user/google-classroom/courses') as Promise<GoogleClassroomCourseListResponse>;
  }
  googleClassroomStudentList(googleCourseId: string, reportError: boolean = false): Promise<GoogleClassroomStudentListResponse> {
    const option: any = {};
    if (reportError) {
      option.responseErrorUse = false;
    }
    return this.apiService.clientGet(`/user/google-classroom/courses/${googleCourseId}/students`, null, null, option) as Promise<GoogleClassroomStudentListResponse>;
  }

  sendRecoverEmail(email: string): Promise<SendRecoverEmailResponse> {
    return this.apiService.clientPost(`/user/send-recover-email`, null, { email }, {
      responseErrorUse: false,
    }) as Promise<SendRecoverEmailResponse>;
  }
  setNewPasswordByRecover(code: string, newPassword: string): Promise<ResetPasswordResponse> {
    return this.apiService.clientPost(`/user/recover-new-pwd`, null, { code, newPassword }, {
      responseErrorUse: false,
    }) as Promise<ResetPasswordResponse>;
  }
  setNewPasswordBySession(currentPassword: string, newPassword: string): Promise<ResetPasswordResponse> {
    return this.apiService.clientPost(`/user/change-pwd`, null, { currentPassword, newPassword }, {
      responseErrorUse: false,
    }) as Promise<ResetPasswordResponse>;
  }
}
